import { formatarCnae, formatarCnpj, limparCnpj, MAX_VENDEDORES_ADICIONAIS, normalizarWhatsapp, validarCnpj, type DadosCnpj } from '@crm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { SITUACAO_COR, useConsultaCnpj, type ResultadoCnpj } from '../cnpj';
import { Erro } from '../components/ui';
import { data, formatarWhatsapp } from '../format';
import { OpcoesStatus, useStatusClientes } from '../status';
import { useTags, useUsuarios } from './Clientes';
import { abertasQueMudam, type NegociacaoCliente } from '../negociacao';
import { AvisoTransferencia } from './CardNegociacoes';

export interface ClienteValores {
  nome: string;
  whatsapp: string;
  empresa: string;
  documento: string;
  email: string;
  cidade: string;
  estado: string;
  segmento: string;
  statusId: string;
  ultimaCompra: string;
  valorUltimaCompra: string;
  vendedorId: string;
  /** Também atendem o cliente, além do principal (specs/22). */
  vendedoresAdicionais: string[];
  tagIds: string[];
  baseLegal: string;
  consentimentoOrigem: string;
  observacoes: string;
}

/** Origens mais comuns do contato (LGPD). "Outro" abre campo livre. */
export const ORIGENS_CONTATO = ['Cliente ativo', 'Indicação', 'Prospecção ativa', 'Evento/feira', 'Site/formulário', 'Outro'] as const;

export const VAZIO: ClienteValores = {
  nome: '', whatsapp: '', empresa: '', documento: '', email: '', cidade: '', estado: '', segmento: '', statusId: '',
  ultimaCompra: '', valorUltimaCompra: '', vendedorId: '', vendedoresAdicionais: [], tagIds: [], baseLegal: 'LEGITIMO_INTERESSE', consentimentoOrigem: '', observacoes: '',
};

export function ClienteForm({
  inicial,
  edicao = false,
  onSalvar,
  onCancelar,
  negociacoes = [],
}: {
  inicial: ClienteValores;
  edicao?: boolean;
  onSalvar: (payload: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
  /** Edição: negociações do cliente, para avisar o que muda de dono ao trocar o vendedor (specs/27) */
  negociacoes?: NegociacaoCliente[];
}) {
  const { pode, me } = useAuth();
  const tags = useTags();
  const usuarios = useUsuarios(pode('CLIENTES_ATRIBUIR'));
  const [v, setV] = useState<ClienteValores>(inicial);
  const statusLista = useStatusClientes();
  const statusPadrao = statusLista.data?.find((s) => s.padraoNovo)?.id ?? '';
  const [erro, setErro] = useState<string | null>(null);
  const [duplicado, setDuplicado] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [novaTag, setNovaTag] = useState('');
  const [origem, setOrigem] = useState<string>('');
  const [origemOutro, setOrigemOutro] = useState('');
  const qc = useQueryClient();

  async function criarTag() {
    if (!novaTag.trim()) return;
    try {
      const t = await api<{ id: string }>('/tags', { body: { nome: novaTag.trim() } });
      await qc.invalidateQueries({ queryKey: ['tags'] });
      setV((x) => ({ ...x, tagIds: [...x.tagIds, t.id] }));
      setNovaTag('');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }
  const set = (k: keyof ClienteValores) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });

  // ---- Consulta de CNPJ (Receita via BrasilAPI/CNPJá): preenche só campos vazios
  const cnpjConsulta = useConsultaCnpj();
  const [preenchidos, setPreenchidos] = useState<string[]>([]);
  const doc = limparCnpj(v.documento);
  const ehCnpj = doc.length === 14;
  const cnpjValido = ehCnpj && validarCnpj(doc);
  // Na edição, não consulta o CNPJ que já estava salvo ao abrir o formulário
  const ultimoConsultado = useRef(edicao ? limparCnpj(inicial.documento) : '');

  const vAtual = useRef(v);
  vAtual.current = v;

  function aplicar(d: DadosCnpj) {
    const x = vAtual.current; // estado mais recente (a consulta é assíncrona)
    const n = { ...x };
    const campos: string[] = [];
    const preencher = (k: 'empresa' | 'cidade' | 'estado', valor: string | null, rotulo: string) => {
      if (valor && !x[k].trim()) {
        n[k] = valor;
        campos.push(rotulo);
      }
    };
    preencher('empresa', d.nomeFantasia ?? d.razaoSocial, 'empresa');
    preencher('cidade', d.endereco.cidade, 'cidade');
    preencher('estado', d.endereco.uf, 'UF');
    setV(n);
    setPreenchidos(campos);
  }

  async function consultarCnpj(forcar = false) {
    if (!cnpjValido || (!forcar && ultimoConsultado.current === doc)) return;
    ultimoConsultado.current = doc;
    setPreenchidos([]);
    const r = await cnpjConsulta.consultar(doc);
    if (r) aplicar(r.dados);
  }

  useEffect(() => {
    if (cnpjValido) void consultarCnpj();
    else if (cnpjConsulta.estado.status !== 'idle') {
      cnpjConsulta.limpar();
      setPreenchidos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, cnpjValido]);
  const tel = v.whatsapp.trim().length >= 8 ? normalizarWhatsapp(v.whatsapp) : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setDuplicado(null);
    if (!edicao && (!origem || (origem === 'Outro' && origemOutro.trim().length < 2))) {
      setErro(origem === 'Outro' ? 'Descreva a origem do contato.' : 'Informe a origem do contato.');
      return;
    }
    setSalvando(true);
    const payload: Record<string, unknown> = {
      nome: v.nome, whatsapp: v.whatsapp, empresa: v.empresa, documento: v.documento, email: v.email, cidade: v.cidade,
      estado: v.estado, segmento: v.segmento, statusId: v.statusId || undefined, ultimaCompra: v.ultimaCompra, valorUltimaCompra: v.valorUltimaCompra.replace(/\./g, '').replace(',', '.'),
      tagIds: v.tagIds, observacoes: v.observacoes,
    };
    if (pode('CLIENTES_ATRIBUIR')) {
      payload.vendedorId = v.vendedorId || null;
      payload.vendedoresAdicionais = v.vendedorId ? v.vendedoresAdicionais.filter((x) => x !== v.vendedorId) : [];
    }
    // Cliente sem WhatsApp (ex.: importado da prospecção) pode ser salvo sem ele; o número é completado depois.
    if (edicao && !v.whatsapp.trim()) delete payload.whatsapp;
    if (!edicao) {
      payload.baseLegal = v.baseLegal;
      payload.consentimentoOrigem = origem === 'Outro' ? `Outro: ${origemOutro.trim()}` : origem;
    }
    try {
      await onSalvar(payload);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'CLIENTE_DUPLICADO' && err.meta?.clienteId) setDuplicado(String(err.meta.clienteId));
      setErro(mensagemErro(err));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form className="col" onSubmit={submit} style={{ gap: 16 }}>
      <Erro erro={erro} />
      {duplicado && <Link to={`/clientes/${duplicado}`}>Abrir o cadastro existente →</Link>}
      <section className="card">
        <h2>Dados do cliente</h2>
        <div className="form-grid">
          <div className="field span-2"><span><label htmlFor="cliente-documento">CNPJ/CPF</label></span>
            <span style={{ display: 'flex', gap: 6 }}>
              <input id="cliente-documento" className="input num" style={{ flex: 1, textTransform: 'uppercase' }} autoCapitalize="characters" spellCheck={false}
                value={v.documento} onChange={set('documento')} placeholder="Digite o CNPJ para buscar na Receita" />
              {cnpjValido && (
                <button type="button" className="btn sm" style={{ height: 38 }} disabled={cnpjConsulta.estado.status === 'carregando'}
                  onClick={() => void consultarCnpj(true)} title="Consultar de novo na Receita">
                  {cnpjConsulta.estado.status === 'carregando' ? 'Buscando…' : 'Buscar'}
                </button>
              )}
            </span>
            {ehCnpj && !cnpjValido && <span className="hint err">CNPJ inválido — confira os dígitos.</span>}
            {cnpjConsulta.estado.status === 'carregando' && <span className="hint">Consultando a Receita Federal…</span>}
            {cnpjConsulta.estado.status === 'erro' && <span className="hint warn">{cnpjConsulta.estado.erro}</span>}
            {preenchidos.length > 0 && <span className="hint">Preenchido pela Receita: {preenchidos.join(', ')}.</span>}
          </div>
          <label className="field"><span>Nome do contato *</span><input className="input" required value={v.nome} onChange={set('nome')} /></label>
          <label className="field"><span>WhatsApp{edicao ? '' : ' *'}</span>
            <input className="input num" required={!edicao} inputMode="tel" value={v.whatsapp} onChange={set('whatsapp')} placeholder="(11) 99876-0001" />
            {tel && (tel.ok
              ? <span className={`hint${tel.aviso ? ' warn' : ''}`}>{tel.aviso ?? `Será salvo como ${formatarWhatsapp(tel.e164)}`}</span>
              : <span className="hint err">{tel.erro}</span>)}
          </label>
          <label className="field"><span>Empresa</span><input className="input" value={v.empresa} onChange={set('empresa')} /></label>
          <label className="field"><span>E-mail</span><input className="input" type="email" value={v.email} onChange={set('email')} /></label>
          <label className="field"><span>Segmento</span><input className="input" value={v.segmento} onChange={set('segmento')} placeholder="Ex.: Varejo, Indústria, Serviços" /></label>
          <label className="field"><span>Cidade</span><input className="input" value={v.cidade} onChange={set('cidade')} /></label>
          <label className="field"><span>UF</span><input className="input" maxLength={2} value={v.estado} onChange={set('estado')} style={{ textTransform: 'uppercase' }} /></label>
          <label className="field"><span>Status</span>
            <select className="select" value={v.statusId || statusPadrao} onChange={set('statusId')}><OpcoesStatus lista={statusLista.data} /></select>
          </label>
          <label className="field"><span>Última compra</span><input className="input" type="date" value={v.ultimaCompra} onChange={set('ultimaCompra')} /></label>
          <label className="field"><span>Valor da última compra (R$)</span><input className="input num" inputMode="decimal" value={v.valorUltimaCompra} onChange={set('valorUltimaCompra')} placeholder="0,00" /></label>
          {pode('CLIENTES_ATRIBUIR') ? (
            <label className="field"><span>Vendedor (principal)</span>
              <select className="select" value={v.vendedorId}
                onChange={(e) => { const id = e.target.value; setV((x) => ({ ...x, vendedorId: id, vendedoresAdicionais: id ? x.vendedoresAdicionais.filter((a) => a !== id) : [] })); }}>
                <option value="">Sem vendedor (fila de distribuição)</option>
                {usuarios.data?.filter((u) => u.status === 'ATIVO').map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
              </select>
            </label>
          ) : (
            <div className="field"><span>Vendedor</span><span style={{ height: 38, display: 'flex', alignItems: 'center' }}>{me?.usuario.nome}</span></div>
          )}
        </div>
        {pode('CLIENTES_ATRIBUIR') && (
          <div className="field">
            <span>Também atendem (opcional)</span>
            {!v.vendedorId ? (
              <span className="hint">Escolha o vendedor principal para adicionar outros.</span>
            ) : (
              <>
                <div className="lista-check" role="group" aria-label="Também atendem">
                  {usuarios.data?.filter((u) => u.status === 'ATIVO' && u.id !== v.vendedorId).map((u) => {
                    const on = v.vendedoresAdicionais.includes(u.id);
                    const bloqueado = !on && v.vendedoresAdicionais.length >= MAX_VENDEDORES_ADICIONAIS;
                    return (
                      <label key={u.id} className={`check${bloqueado ? ' off' : ''}`}>
                        <input type="checkbox" checked={on} disabled={bloqueado}
                          onChange={() => setV((x) => ({ ...x, vendedoresAdicionais: on ? x.vendedoresAdicionais.filter((a) => a !== u.id) : [...x.vendedoresAdicionais, u.id] }))} />
                        {u.nome}
                      </label>
                    );
                  })}
                </div>
                <span className="hint">
                  {v.vendedoresAdicionais.length} de {MAX_VENDEDORES_ADICIONAIS} · eles também veem a ficha e os contatos; cada um vê e move só as negociações dele, e a venda que registrarem conta para eles.
                </span>
              </>
            )}
          </div>
        )}
        {edicao && pode('CLIENTES_ATRIBUIR') && (
          <AvisoTransferencia negs={abertasQueMudam(negociacoes, v.vendedorId || null, v.vendedoresAdicionais)}
            para={usuarios.data?.find((u) => u.id === v.vendedorId)?.nome ?? 'o novo vendedor'} />
        )}
        {cnpjConsulta.estado.status === 'ok' && (
          <PainelReceita
            r={cnpjConsulta.estado.resultado}
            whatsappVazio={!v.whatsapp.trim()}
            emailVazio={!v.email.trim()}
            onUsarTelefone={(t) => setV((x) => ({ ...x, whatsapp: t }))}
            onUsarEmail={(e) => setV((x) => ({ ...x, email: e }))}
          />
        )}
        {(!!tags.data?.length || pode('TAGS_GERENCIAR')) && (
          <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
            <legend className="small muted" style={{ marginBottom: 8, padding: 0 }}>Tags</legend>
            {pode('TAGS_GERENCIAR') && (
              <span style={{ display: 'flex', gap: 6 }}>
                <input className="input" style={{ width: 160, height: 32 }} aria-label="Nova tag" placeholder="Nova tag" value={novaTag}
                  onChange={(e) => setNovaTag(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void criarTag(); } }} />
                <button type="button" className="btn sm" onClick={criarTag} disabled={!novaTag.trim()}>Criar</button>
              </span>
            )}
            {tags.data?.map((t) => (
              <label key={t.id} className="check">
                <input type="checkbox" checked={v.tagIds.includes(t.id)}
                  onChange={(e) => setV({ ...v, tagIds: e.target.checked ? [...v.tagIds, t.id] : v.tagIds.filter((x) => x !== t.id) })} />
                {t.nome}
              </label>
            ))}
          </fieldset>
        )}
        <label className="field"><span>Observações</span><textarea className="textarea" rows={3} value={v.observacoes} onChange={set('observacoes')} /></label>
      </section>

      {!edicao && (
        <section className="card">
          <h2>LGPD</h2>
          <div className="form-grid">
            <label className="field"><span>Base legal *</span>
              <select className="select" value={v.baseLegal} onChange={set('baseLegal')}>
                <option value="LEGITIMO_INTERESSE">Legítimo interesse (cliente da carteira)</option>
                <option value="CONSENTIMENTO">Consentimento</option>
                <option value="EXECUCAO_CONTRATO">Execução de contrato</option>
              </select>
            </label>
          </div>
          <div className="field"><span>Origem do contato *</span>
            <div className="opts">
              {ORIGENS_CONTATO.map((o) => (
                <button type="button" key={o} className={`opt${origem === o ? ' on' : ''}`} aria-pressed={origem === o}
                  onClick={() => {
                    setOrigem(o);
                    // Formulário do site normalmente vem com aceite explícito
                    if (o === 'Site/formulário') setV((x) => ({ ...x, baseLegal: 'CONSENTIMENTO' }));
                    else if (origem === 'Site/formulário') setV((x) => ({ ...x, baseLegal: 'LEGITIMO_INTERESSE' }));
                  }}>{o}</button>
              ))}
            </div>
            {origem === 'Outro' && (
              <input className="input" autoFocus maxLength={180} value={origemOutro} onChange={(e) => setOrigemOutro(e.target.value)} placeholder="De onde veio este contato?" />
            )}
            <span className="hint">Fica registrado como prova de onde veio o contato, caso o cliente pergunte.</span>
          </div>
        </section>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onCancelar}>Cancelar</button>
        <button className="btn primary" disabled={salvando}>{salvando ? 'Salvando…' : edicao ? 'Salvar alterações' : 'Cadastrar cliente'}</button>
      </div>
    </form>
  );
}

const SITUACAO_TEXTO: Record<string, string> = {
  SUSPENSA: 'suspenso', INAPTA: 'inapto', BAIXADA: 'baixado (empresa encerrada)', NULA: 'nulo', DESCONHECIDA: 'com situação desconhecida',
};

/** Dados da Receita. Telefone e e-mail são só SUGESTÃO: na Receita costumam ser do contador. */
function PainelReceita({
  r, whatsappVazio, emailVazio, onUsarTelefone, onUsarEmail,
}: {
  r: ResultadoCnpj;
  whatsappVazio: boolean;
  emailVazio: boolean;
  onUsarTelefone: (t: string) => void;
  onUsarEmail: (e: string) => void;
}) {
  const d = r.dados;
  const e = d.endereco;
  const endereco = [
    [e.logradouro, e.numero].filter(Boolean).join(', '),
    e.complemento, e.bairro, [e.cidade, e.uf].filter(Boolean).join('/'), e.cep && `CEP ${e.cep.replace(/^(\d{5})(\d{3})$/, '$1-$2')}`,
  ].filter(Boolean).join(' · ');
  return (
    <div className="col" style={{ gap: 10, padding: 14, border: '1px solid var(--border-2)', borderRadius: 10, background: 'var(--surface-2)' }}>
      {d.situacao !== 'ATIVA' && (
        <div className="alert danger" role="alert">
          <span>
            CNPJ <b>{SITUACAO_TEXTO[d.situacao] ?? d.situacao}</b> na Receita{d.situacaoData ? ` desde ${data(d.situacaoData)}` : ''}.
            Confirme com o cliente antes de vender ou faturar.
          </span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13.5 }}>Receita Federal</b>
        <span className={`badge ${SITUACAO_COR[d.situacao] ?? ''}`}>{d.situacao}</span>
        <span className="small muted num">{formatarCnpj(d.cnpj)}</span>
        {r.desatualizado && <span className="badge amber" title="Os serviços de consulta estão fora do ar; exibindo a última consulta salva.">dados podem estar desatualizados</span>}
      </div>
      <dl className="dados" style={{ margin: 0 }}>
        <dt>Razão social</dt><dd>{d.razaoSocial}</dd>
        {d.nomeFantasia && <><dt>Nome fantasia</dt><dd>{d.nomeFantasia}</dd></>}
        {d.cnaePrincipal && <><dt>Atividade</dt><dd><span className="num">{formatarCnae(d.cnaePrincipal.codigo)}</span> · {d.cnaePrincipal.descricao}</dd></>}
        {endereco && <><dt>Endereço</dt><dd>{endereco}</dd></>}
        {d.abertura && <><dt>Abertura</dt><dd className="num">{data(d.abertura)}</dd></>}
        {d.telefones.length > 0 && (
          <><dt>Telefone</dt><dd style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {d.telefones.map((t) => (
              <span key={t} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <span className="num">{t.replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3')}</span>
                {whatsappVazio && <button type="button" className="btn sm" onClick={() => onUsarTelefone(t)}>Usar como WhatsApp</button>}
              </span>
            ))}
          </dd></>
        )}
        {d.email && (
          <><dt>E-mail</dt><dd style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {d.email}
            {emailVazio && <button type="button" className="btn sm" onClick={() => onUsarEmail(d.email!)}>Usar e-mail</button>}
          </dd></>
        )}
      </dl>
      {(d.telefones.length > 0 || d.email) && (
        <span className="small muted">Telefone e e-mail da Receita costumam ser do contador — confirme antes de usar.</span>
      )}
    </div>
  );
}
